/**
 * "Nachbereitung" of a past substitution inquiry (panel of "Vertretungen", DetailFollowUpCard): texts of the guided steps
 * Was ist passiert? → Kompensation / Abrechnung → Nachbereitung abgeschlossen. Pure, checked in
 * tests/substitutions/followUp.check.ts. Backend: substitution-manager src/follow-up (GET/PUT :id/follow-up, done, reopen).
 */
import moment from 'moment';
import {
  SUBSTITUTION_FOLLOW_UP_OUTCOME_LABELS,
  SubstitutionFollowUpOutcome,
} from '@/enums/substitution-manager/SubstitutionFollowUp';
import {
  SubstitutionFollowUpResponseDto,
  SubstitutionOverviewPerson,
} from '@/classes/dto/substitution-manager/overview/SubstitutionOverview.dto';

const O = SubstitutionFollowUpOutcome;

type Tone = 'info' | 'warn' | 'good' | 'muted' | 'bad';
type Timetable = SubstitutionFollowUpResponseDto['timetable'];

const name = (person: { firstName?: string; lastName?: string } | null | undefined, fallback = 'eine Lehrkraft') =>
  `${person?.firstName || ''} ${person?.lastName || ''}`.trim() || fallback;

const names = (people: Array<{ firstName?: string; lastName?: string }>) => people.map((p) => name(p)).join(', ');

/** Outcome the timetable points to (office still decides) */
export function suggestedOutcome(kind: Timetable['kind']): SubstitutionFollowUpOutcome | undefined {
  return kind === 'none' ? O.CANCELLED : kind === 'absentTeacher' ? O.HELD_BY_TEACHER : kind === 'other' ? O.SUBSTITUTED : undefined;
}

export interface OutcomeChoice {
  value: SubstitutionFollowUpOutcome;
  text: string;
  hint: string;
  suggested: boolean;
}

export function outcomeChoices(kind: Timetable['kind']): OutcomeChoice[] {
  const suggested = suggestedOutcome(kind);
  return [
    {value: O.CANCELLED, text: SUBSTITUTION_FOLLOW_UP_OUTCOME_LABELS[O.CANCELLED], hint: 'Niemand hat unterrichtet – danach die Kompensation festlegen'},
    {value: O.SUBSTITUTED, text: SUBSTITUTION_FOLLOW_UP_OUTCOME_LABELS[O.SUBSTITUTED], hint: 'Eine andere Lehrkraft hat unterrichtet, ohne Zusage in Vanilla'},
    {value: O.HELD_BY_TEACHER, text: SUBSTITUTION_FOLLOW_UP_OUTCOME_LABELS[O.HELD_BY_TEACHER], hint: 'Die Lehrkraft ist doch gekommen'},
  ].map((choice) => ({...choice, suggested: choice.value === suggested}));
}

/** "Laut Stundenplan …" + course logs of the day (hint above step 1) */
export function evidenceText(timetable: Timetable, courseLogs: SubstitutionFollowUpResponseDto['courseLogs'], absentName: string): string {
  const parts: string[] = [];
  switch (timetable.kind) {
    case 'none':
      parts.push('Laut Stundenplan hat an dem Tag niemand unterrichtet (Stunde ohne Lehrkraft).');
      break;
    case 'absentTeacher':
      parts.push(`Laut Stundenplan unterrichtet ${absentName || 'die Lehrkraft'} die Stunde selbst.`);
      break;
    case 'other':
      parts.push(`Laut Stundenplan unterrichtet ${names(timetable.teachers)} die Stunde.`);
      break;
    case 'mixed':
      parts.push(`Laut Stundenplan unterschiedlich je Kurs${timetable.teachers.length ? ` (${names(timetable.teachers)} und ohne Lehrkraft)` : ''}.`);
      break;
    default:
      parts.push('Die Stunde wurde im Stundenplan nicht gefunden.');
  }
  if (courseLogs.length) {
    parts.push(`Kursprotokoll von ${courseLogs.map((log) => `${name(log)} (${log.count})`).join(', ')} – die Stunde hat wohl stattgefunden.`);
  } else {
    parts.push('Für den Tag gibt es kein Kursprotokoll.');
  }
  return parts.join(' ');
}

export interface PayoffHint {
  text: string;
  tone: Tone;
  /** The teacher of the lesson must be corrected in the calendar for the payoff */
  calendar: boolean;
}

/**
 * Step 2 of "vertreten" / "selbst gehalten": the payoff pays whoever teaches the lesson in the timetable – does that match?
 * (Nothing is changed automatically.)
 */
export function payoffHint(outcome: SubstitutionFollowUpOutcome | undefined, timetable: Timetable, absentId: string, substitute?: SubstitutionOverviewPerson | null): PayoffHint | undefined {
  if (!outcome || outcome === O.CANCELLED) {
    return undefined;
  }
  const teacherIds = timetable.teachers.map((t) => String(t._id));
  if (timetable.kind === 'unknown') {
    return {text: 'Die Stunde wurde im Stundenplan nicht gefunden – bitte die Abrechnung der Stunde selbst prüfen.', tone: 'warn', calendar: true};
  }
  if (outcome === O.HELD_BY_TEACHER) {
    return timetable.kind === 'absentTeacher'
      ? {text: 'Passt: Im Stundenplan steht die Lehrkraft selbst – sie bekommt die Stunde bezahlt.', tone: 'good', calendar: false}
      : {text: 'Im Stundenplan steht die Lehrkraft für diesen Tag nicht. Damit sie die Stunde bezahlt bekommt, bitte im Kalender die Lehrkraft der Stunde wieder eintragen.',
        tone: 'warn', calendar: true};
  }
  if (timetable.kind === 'other' && (!substitute || teacherIds.includes(String(substitute._id))) && !teacherIds.includes(absentId)) {
    return {text: `Passt: Im Stundenplan steht ${names(timetable.teachers)} – bekommt die Stunde bezahlt.`, tone: 'good', calendar: false};
  }
  const who = substitute ? name(substitute) : 'die Vertretung';
  return {text: `Im Stundenplan steht ${timetable.kind === 'none' ? 'niemand' : names(timetable.teachers) || 'jemand anderes'}. Damit ${who} die Stunde bezahlt bekommt, bitte im Kalender die Lehrkraft der Stunde eintragen.`,
    tone: 'warn', calendar: true};
}

/** Status chip of the card */
export function followUpStatus(followUp: SubstitutionFollowUpResponseDto['followUp'] | undefined): { text: string; tone: Tone } {
  if (followUp?.doneAt) {
    return {text: 'Abgeschlossen', tone: 'good'};
  }
  return {text: 'Offen', tone: 'warn'};
}

/** "Abgeschlossen von Tanja Büro am 13.10.2026, 10:15 Uhr" */
export function doneText(followUp: SubstitutionFollowUpResponseDto['followUp'] | undefined): string {
  if (!followUp?.doneAt) {
    return '';
  }
  return `Abgeschlossen${followUp.doneByName ? ` von ${followUp.doneByName}` : ''} am ${moment(followUp.doneAt).format('DD.MM.YYYY, HH:mm [Uhr]')}`;
}

/** "Angegeben von … am …" under step 1 */
export function outcomeByText(followUp: SubstitutionFollowUpResponseDto['followUp'] | undefined): string {
  if (!followUp?.outcome || !followUp.outcomeAt) {
    return '';
  }
  return `angegeben${followUp.outcomeByName ? ` von ${followUp.outcomeByName}` : ''} am ${moment(followUp.outcomeAt).format('DD.MM.YYYY, HH:mm [Uhr]')}`;
}

/** Teachers to pick as "vertreten von" (timetable first, then asked / confirmed ones), without the absent teacher */
export function substituteOptions(timetableTeachers: SubstitutionOverviewPerson[], others: SubstitutionOverviewPerson[], absentId: string): Array<{ value: string; text: string }> {
  const seen = new Set<string>([absentId]);
  const out: Array<{ value: string; text: string }> = [];
  for (const person of [...timetableTeachers, ...others]) {
    const id = String(person?._id || '');
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push({value: id, text: name(person, 'Lehrkraft')});
  }
  return out;
}

/** "Nachbereitung" in the row: "Was ist passiert? – bitte nachbereiten" / "Stunde ist ausgefallen – noch abschließen" / "Nachbereitet: …" */
export function followUpRowText(followUp: SubstitutionFollowUpResponseDto['followUp'] | null | undefined, done: boolean): string {
  const outcome = followUp?.outcome ? SUBSTITUTION_FOLLOW_UP_OUTCOME_LABELS[followUp.outcome] : '';
  if (done) {
    const by = followUp?.doneByName ? ` von ${followUp.doneByName}` : '';
    const when = followUp?.doneAt ? ` am ${moment(followUp.doneAt).format('DD.MM.')}` : '';
    return `Nachbereitet${by}${when}${outcome ? ` · ${outcome}` : ''}`;
  }
  return outcome ? `${outcome} – Nachbereitung noch abschließen` : 'Was ist passiert? Bitte nachbereiten';
}

/** Link of the to-do "Nachbereitung offen" */
export const FOLLOW_UP_TODO_LINK_TEXT = 'Nachbereitung öffnen';
